import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

import TransactionHistoryItem from './components/TransactionHistoryItem';
import FeeCalculator from './components/FeeCalculator';
import AddPaymentMethodModal from './components/AddPaymentMethodModal';
import SecurityVerificationModal from './components/SecurityVerificationModal';
import QuickActionCard from './components/QuickActionCard';

const FundingPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('deposit');
  const [isAddMethodModalOpen, setIsAddMethodModalOpen] = useState(false);
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);

  const paymentMethods = [
    {
      id: 1,
      type: 'bank',
      name: 'Chase Checking',
      details: '****4892',
      status: 'verified',
      isDefault: true,
      addedDate: 'Jan 5, 2026',
      limits: {
        daily: 50000,
        monthly: 250000
      },
      processingTime: '3-5 business days'
    },
    {
      id: 2,
      type: 'bank',
      name: 'Bank of America Savings',
      details: '****7231',
      status: 'verified',
      isDefault: false,
      addedDate: 'Dec 28, 2025',
      limits: {
        daily: 25000,
        monthly: 100000
      },
      processingTime: '3-5 business days'
    },
    {
      id: 3,
      type: 'card',
      name: 'Visa Debit Card',
      details: '****3456',
      status: 'verified',
      isDefault: false,
      addedDate: 'Jan 10, 2026',
      limits: {
        daily: 10000,
        monthly: 50000
      },
      processingTime: 'Instant'
    },
    {
      id: 4,
      type: 'wire',
      name: 'Wire Transfer',
      details: 'International transfers',
      status: 'pending',
      isDefault: false,
      addedDate: 'Jan 12, 2026',
      limits: {
        daily: 100000,
        monthly: 500000
      },
      processingTime: '1-2 business days'
    }
  ];

  const transactionHistory = [
    {
      id: 1,
      type: 'deposit',
      description: 'ACH Deposit',
      method: 'Chase Checking ****4892',
      amount: 5000,
      status: 'completed',
      date: 'Jan 11, 2026 2:45 PM',
      transactionId: 'TXN-2026-001234'
    },
    {
      id: 2,
      type: 'withdrawal',
      description: 'Withdrawal to Bank',
      method: 'Bank of America ****7231',
      amount: 1500,
      status: 'processing',
      date: 'Jan 12, 2026 10:30 AM',
      transactionId: 'TXN-2026-001235',
      estimatedCompletion: 'Jan 15, 2026'
    },
    {
      id: 3,
      type: 'deposit',
      description: 'Debit Card Deposit',
      method: 'Visa ****3456',
      amount: 500,
      status: 'completed',
      date: 'Jan 10, 2026 4:15 PM',
      transactionId: 'TXN-2026-001233'
    },
    {
      id: 4,
      type: 'deposit',
      description: 'Wire Transfer',
      method: 'Wire Transfer',
      amount: 25000,
      status: 'pending',
      date: 'Jan 9, 2026 9:00 AM',
      transactionId: 'TXN-2026-001232',
      estimatedCompletion: 'Jan 13, 2026'
    },
    {
      id: 5,
      type: 'withdrawal',
      description: 'Withdrawal to Bank',
      method: 'Chase Checking ****4892',
      amount: 2000,
      status: 'completed',
      date: 'Jan 8, 2026 11:20 AM',
      transactionId: 'TXN-2026-001231'
    }
  ];

  const quickActions = [
    {
      title: 'Schedule Withdrawal',
      description: 'Plan your withdrawals in advance for better fund management',
      icon: 'Calendar',
      iconColor: 'var(--color-secondary)',
      buttonText: 'Schedule',
      onClick: () => {
        setActiveTab('withdraw');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    {
      title: 'View Limits',
      description: 'Check your daily and monthly transaction limits',
      icon: 'Shield',
      iconColor: 'var(--color-brand-trust)',
      buttonText: 'View Details',
      onClick: () => {
        const limitsSection = document.getElementById('limits-section');
        limitsSection?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  ];

  const methodOptions = paymentMethods?.filter(m => m?.status === 'verified')?.map(m => ({
      value: m?.id?.toString(),
      label: `${m?.name} (${m?.details})`,
      description: m?.processingTime
    }));

  const handleRemoveMethod = (id) => {
    console.log('Remove method:', id);
  };

  const handleSetDefault = (id) => {
    console.log('Set default method:', id);
  };

  const handleAddMethod = (methodData) => {
    console.log('Add new method:', methodData);
  };

  const handleTransaction = (e) => {
    e?.preventDefault();
    const amount = parseFloat(selectedAmount);

    // Security verification for large transactions
    if (amount > 10000) {
      setIsSecurityModalOpen(true);
    } else {
      console.log('Process transaction:', { amount, method: selectedMethod, type: activeTab });
    }
  };

  const handleSecurityVerification = () => {
    console.log('Transaction verified and processed');
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center">
                <Icon name="Wallet" size={24} color="#FFFFFF" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[var(--color-foreground)]">
                  Funding Portal
                </h1>
                <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mt-1">
                  Manage your deposits, withdrawals, and payment methods
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-brand-trust)] rounded-lg p-4 md:p-6 text-white">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm opacity-90">Available Balance</p>
                <Icon name="DollarSign" size={20} color="#FFFFFF" />
              </div>
              <p className="text-3xl md:text-4xl font-bold mb-2">$0.00</p>
              <p className="text-xs opacity-75">USD Balance</p>
            </div>

            <div className="bg-gradient-to-br from-[var(--color-secondary)] to-[var(--color-success)] rounded-lg p-4 md:p-6 text-white">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm opacity-90">Total Deposited</p>
                <Icon name="TrendingUp" size={20} color="#FFFFFF" />
              </div>
              <p className="text-3xl md:text-4xl font-bold mb-2">$0.00</p>
              <p className="text-xs opacity-75">This month</p>
            </div>

            <div className="bg-gradient-to-br from-[var(--color-accent)] to-[var(--color-warning)] rounded-lg p-4 md:p-6 text-white">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm opacity-90">Pending Transactions</p>
                <Icon name="Clock" size={20} color="#FFFFFF" />
              </div>
              <p className="text-3xl md:text-4xl font-bold mb-2">0</p>
              <p className="text-xs opacity-75">In progress</p>
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-[var(--color-foreground)] mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {quickActions?.map((action, index) => (
                <QuickActionCard key={index} {...action} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-[var(--color-card)] rounded-lg shadow-md overflow-hidden">
                <div className="border-b border-[var(--color-border)]">
                  <div className="flex overflow-x-auto">
                    <button
                      onClick={() => setActiveTab('deposit')}
                      className={`flex-shrink-0 px-6 py-4 text-sm md:text-base font-medium transition-colors duration-300 ${
                        activeTab === 'deposit' ?'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]' :'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="ArrowDownToLine" size={18} />
                        <span>Deposit</span>
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('withdraw')}
                      className={`flex-shrink-0 px-6 py-4 text-sm md:text-base font-medium transition-colors duration-300 ${
                        activeTab === 'withdraw' ?'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]' :'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="ArrowUpFromLine" size={18} />
                        <span>Withdraw</span>
                      </div>
                    </button>
                  </div>
                </div>

                <form onSubmit={handleTransaction} className="p-4 md:p-6 space-y-4">
                  <Input
                    label="Amount (USD)"
                    type="number"
                    placeholder="Enter amount"
                    value={selectedAmount}
                    onChange={(e) => setSelectedAmount(e?.target?.value)}
                    required
                    min="1"
                    step="0.01"
                  />

                  <Select
                    label="Withdrawal Method"
                    options={methodOptions}
                    value={selectedMethod}
                    onChange={setSelectedMethod}
                    placeholder="Select payment method"
                    required
                  />

                  <div className="p-4 bg-[var(--color-muted)] rounded-lg">
                    <div className="flex items-start gap-3">
                      <Icon name="Info" size={18} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                      <div className="space-y-2">
                        <p className="text-sm text-[var(--color-foreground)] font-medium">
                          {activeTab === 'deposit' ? 'Deposit Information' : 'Withdrawal Information'}
                        </p>
                        <ul className="text-xs text-[var(--color-muted-foreground)] space-y-1">
                          <li>• ACH transfers: 3-5 business days, no fees</li>
                          <li>• Wire transfers: 1-2 business days, $25 fee</li>
                          <li>• Debit card: Instant, 3.99% fee</li>
                          <li>• Transactions over $10,000 require security verification</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      fullWidth
                      onClick={() => {
                        setSelectedAmount('');
                        setSelectedMethod('');
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      variant="default"
                      fullWidth
                      iconName={activeTab === 'deposit' ? 'ArrowDownToLine' : 'ArrowUpFromLine'}
                      iconPosition="left"
                    >
                      {activeTab === 'deposit' ? 'Deposit Funds' : 'Withdraw Funds'}
                    </Button>
                  </div>
                </form>
              </div>

              <div id="limits-section" className="bg-[var(--color-card)] rounded-lg shadow-md p-4 md:p-6">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-brand-trust)] bg-opacity-10 flex items-center justify-center">
                    <Icon name="Shield" size={20} color="var(--color-brand-trust)" />
                  </div>
                  <div>
                    <h2 className="text-lg md:text-xl font-semibold text-[var(--color-foreground)]">
                      Transaction Limits
                    </h2>
                    <p className="text-xs md:text-sm text-[var(--color-muted-foreground)]">
                      Your current limits and usage
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[var(--color-muted)] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-[var(--color-muted-foreground)]">Daily Limit</p>
                      <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                    </div>
                    <p className="text-2xl font-bold text-[var(--color-foreground)] mb-1">
                      $50,000
                    </p>
                    <div className="w-full bg-[var(--color-border)] rounded-full h-2 mb-2">
                      <div className="bg-[var(--color-primary)] h-2 rounded-full" style={{ width: '30%' }}></div>
                    </div>
                    <p className="text-xs text-[var(--color-muted-foreground)]">
                      $15,000 used today
                    </p>
                  </div>

                  <div className="p-4 bg-[var(--color-muted)] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-[var(--color-muted-foreground)]">Monthly Limit</p>
                      <Icon name="CalendarDays" size={16} color="var(--color-muted-foreground)" />
                    </div>
                    <p className="text-2xl font-bold text-[var(--color-foreground)] mb-1">
                      $250,000
                    </p>
                    <div className="w-full bg-[var(--color-border)] rounded-full h-2 mb-2">
                      <div className="bg-[var(--color-secondary)] h-2 rounded-full" style={{ width: '45%' }}></div>
                    </div>
                    <p className="text-xs text-[var(--color-muted-foreground)]">
                      $112,500 used this month
                    </p>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-[var(--color-primary)] bg-opacity-5 rounded-lg border border-[var(--color-primary)] border-opacity-20">
                  <div className="flex items-start gap-2">
                    <Icon name="TrendingUp" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[var(--color-muted-foreground)]">
                      Complete identity verification to increase your limits up to $500,000 monthly
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[var(--color-card)] rounded-lg shadow-md p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                      <Icon name="History" size={20} color="var(--color-accent)" />
                    </div>
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-[var(--color-foreground)]">
                        Transaction History
                      </h2>
                      <p className="text-xs md:text-sm text-[var(--color-muted-foreground)]">
                        Recent deposits and withdrawals
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTransactionHistory(!showTransactionHistory)}
                    iconName={showTransactionHistory ? 'ChevronUp' : 'ChevronDown'}
                    iconPosition="right"
                  >
                    {showTransactionHistory ? 'Hide' : 'Show All'}
                  </Button>
                </div>

                <div className="space-y-3">
                  {(showTransactionHistory ? transactionHistory : transactionHistory?.slice(0, 3))?.map((transaction) => (
                    <TransactionHistoryItem key={transaction?.id} transaction={transaction} />
                  ))}
                </div>

                {!showTransactionHistory && transactionHistory?.length > 3 && (
                  <div className="mt-4 text-center">
                    <Button
                      variant="ghost"
                      onClick={() => setShowTransactionHistory(true)}
                      iconName="ChevronDown"
                      iconPosition="right"
                    >
                      View {transactionHistory?.length - 3} more transactions
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <FeeCalculator />

              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-4 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-success)] bg-opacity-10 flex items-center justify-center">
                    <Icon name="ShieldCheck" size={20} color="var(--color-success)" />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-[var(--color-foreground)]">
                      Security Features
                    </h3>
                    <p className="text-xs md:text-sm text-[var(--color-muted-foreground)]">
                      Your funds are protected
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Icon name="Lock" size={16} color="var(--color-success)" className="flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">
                        Bank-Level Encryption
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
                        256-bit SSL encryption for all transactions
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Shield" size={16} color="var(--color-success)" className="flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">
                        FDIC Insured
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
                        USD deposits insured up to $250,000
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={16} color="var(--color-success)" className="flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">
                        Fraud Detection
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
                        Real-time monitoring for suspicious activity
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">
                        PCI DSS Compliant
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
                        Industry-standard payment security
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-brand-trust)] rounded-lg p-4 md:p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="Headphones" size={24} color="#FFFFFF" />
                  <h3 className="text-base md:text-lg font-semibold">
                    Need Help?
                  </h3>
                </div>
                <p className="text-sm opacity-90 mb-4">
                  Our US-based support team is available 24/7 to assist with your funding needs
                </p>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => navigate('/security-center')}
                  className="bg-white text-[var(--color-primary)] hover:bg-opacity-90"
                >
                  Contact Support
                </Button>
              </div>

              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-4 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-warning)] bg-opacity-10 flex items-center justify-center">
                    <Icon name="FileText" size={20} color="var(--color-warning)" />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-[var(--color-foreground)]">
                      Regulatory Compliance
                    </h3>
                  </div>
                </div>

                <div className="space-y-3 text-xs text-[var(--color-muted-foreground)]">
                  <p>
                    Kryptostash is registered with FinCEN as a Money Services Business and complies with all applicable federal and state regulations.
                  </p>
                  <p>
                    All transactions are subject to anti-money laundering (AML) and know-your-customer (KYC) requirements as mandated by US law.
                  </p>
                  <div className="flex items-center gap-2 pt-2">
                    <Icon name="ExternalLink" size={14} color="var(--color-primary)" />
                    <a href="#" className="text-[var(--color-primary)] hover:underline">
                      View full compliance documentation
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <AddPaymentMethodModal
        isOpen={isAddMethodModalOpen}
        onClose={() => setIsAddMethodModalOpen(false)}
        onAdd={handleAddMethod}
      />
      <SecurityVerificationModal
        isOpen={isSecurityModalOpen}
        onClose={() => setIsSecurityModalOpen(false)}
        onVerify={handleSecurityVerification}
        amount={parseFloat(selectedAmount)}
      />
    </div>
  );
};

export default FundingPortal;